/* import __COLOCATED_TEMPLATE__ from './identity-verification-secrets.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import { deleteRequest } from 'embercom/lib/inbox/requests';
import type MessengerApiSecret from 'embercom/models/messenger-security/messenger-api-secret';
const MAX_SECRET_KEYS = 15;

export default class IdentityVerificationSecrets extends Component {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @tracked isLoading = true;
  @tracked showCreateIdvSecretKeyModal = false;
  @tracked secretKeyName = '';
  @tracked isWebEnabled = false;
  @tracked isIosEnabled = false;
  @tracked isAndroidEnabled = false;
  @tracked selectedSecretKey?: MessengerApiSecret;
  @tracked showDeleteIdvSecretKeyModal = false;

  get hasSelectedPlatform() {
    return this.isWebEnabled || this.isIosEnabled || this.isAndroidEnabled;
  }

  get hasValidName() {
    return this.secretKeyName.trim().length > 0;
  }

  get canSubmitForm() {
    return this.hasSelectedPlatform && this.hasValidName;
  }

  constructor(owner: unknown, args: never) {
    super(owner, args);
    this.loadIdentitySettingsTokens();
  }

  async loadIdentitySettingsTokens() {
    this.isLoading = true;
    await this.store.findAll('messenger-security/messenger-api-secret');
    this.isLoading = false;
  }

  get sortedTokens() {
    return this.store
      .peekAll('messenger-security/messenger-api-secret')
      .sortBy('createdAt')
      .reverse()
      .sortBy('deleted');
  }

  get canCreateSecretKey() {
    return this.sortedTokens.filter((token) => !token.deleted).length < MAX_SECRET_KEYS;
  }

  @action
  availablePlatforms(messengerApiSecret: MessengerApiSecret) {
    let platforms = [
      {
        label: this.intl.t('apps.app.settings.security.idv-setting-table.android'),
        value: 'android',
        checked: messengerApiSecret.supportsAndroid,
      },
      {
        label: this.intl.t('apps.app.settings.security.idv-setting-table.ios'),
        value: 'ios',
        checked: messengerApiSecret.supportsIos,
      },
      {
        label: this.intl.t('apps.app.settings.security.idv-setting-table.web'),
        value: 'web',
        checked: messengerApiSecret.supportsWeb,
      },
    ];

    if (platforms.every((platform) => platform.checked)) {
      return this.intl.t('apps.app.settings.security.idv-setting-table.all');
    }

    return platforms
      .filter((platform) => platform.checked)
      .map((platform) => platform.label)
      .join(', ');
  }

  @action
  async createSecretKey() {
    let newToken = this.store.createRecord('messenger-security/messenger-api-secret', {
      name: this.secretKeyName,
      supportsAndroid: this.isAndroidEnabled,
      supportsIos: this.isIosEnabled,
      supportsWeb: this.isWebEnabled,
    });

    try {
      await newToken.save();

      this.intercomEventService.trackAnalyticsEvent({
        action: 'created',
        object: 'identity_verification_key',
        section: 'security',
        name: this.secretKeyName,
        supports_android: this.isAndroidEnabled,
        supports_ios: this.isIosEnabled,
        supports_web: this.isWebEnabled,
      });

      this.resetForm();
      this.showCreateIdvSecretKeyModal = false;
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'apps.app.settings.security.idv-create-secret-key-modal.success-creating-secret-key',
        ),
      );
    } catch (error) {
      console.error(error);
      this.notificationsService.notifyError(
        this.intl.t(
          'apps.app.settings.security.idv-create-secret-key-modal.error-creating-secret-key',
        ),
      );
    }
  }

  @action
  toggleDeleteIdvSecretKeyModal(messengerApiSecret: MessengerApiSecret) {
    this.selectedSecretKey = messengerApiSecret;
    this.showDeleteIdvSecretKeyModal = !this.showDeleteIdvSecretKeyModal;
  }

  @action
  async deleteSecretKey(messengerApiSecret: MessengerApiSecret) {
    await deleteRequest(
      `/ember/messenger_security/messenger_api_secrets/${messengerApiSecret.id}`,
      {
        app_id: this.appService.app.id,
      },
    );

    this.intercomEventService.trackAnalyticsEvent({
      action: 'deleted',
      object: 'identity_verification_key',
      section: 'security',
      key_id: messengerApiSecret.id,
      key_name: messengerApiSecret.name,
      supports_android: messengerApiSecret.supportsAndroid,
      supports_ios: messengerApiSecret.supportsIos,
      supports_web: messengerApiSecret.supportsWeb,
      last_used_at: messengerApiSecret.lastUsedAt,
    });

    messengerApiSecret.deleted = true;
    this.showDeleteIdvSecretKeyModal = false;
    this.notificationsService.notifyConfirmation(
      this.intl.t('apps.app.settings.security.idv-setting-table.delete-success'),
    );
    this.selectedSecretKey = undefined;
  }

  resetForm() {
    this.secretKeyName = '';
    this.isWebEnabled = false;
    this.isIosEnabled = false;
    this.isAndroidEnabled = false;
  }

  get activeTokens() {
    return this.sortedTokens.filter((token) => !token.deleted);
  }

  @action
  isLastTokenForPlatform(messengerApiSecret: MessengerApiSecret) {
    let isLastTokenForAndroid = false;
    let isLastTokenForIos = false;
    let isLastTokenForWeb = false;
    if (messengerApiSecret.supportsAndroid) {
      isLastTokenForAndroid =
        this.activeTokens.filter((token) => token.supportsAndroid).length === 1;
    }
    if (messengerApiSecret.supportsIos) {
      isLastTokenForIos = this.activeTokens.filter((token) => token.supportsIos).length === 1;
    }
    if (messengerApiSecret.supportsWeb) {
      isLastTokenForWeb = this.activeTokens.filter((token) => token.supportsWeb).length === 1;
    }
    return isLastTokenForAndroid || isLastTokenForIos || isLastTokenForWeb;
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Security::IdentityVerificationSecrets': typeof IdentityVerificationSecrets;
    'new-settings/workspace/security/identity-verification-secrets': typeof IdentityVerificationSecrets;
  }
}
